export type Language = "ar" | "en";
export type ModuleKey =
  | "sales"
  | "inventory"
  | "branches"
  | "customers"
  | "reports"
  | "team"
  | "operations"
  | "devices";

export const TERMS_VERSION = "terms-v1";
export const PRIVACY_VERSION = "privacy-v1";
export const RESEND_COOLDOWN_SECONDS = 60;

export const uiCopy = {
  ar: {
    signIn: "تسجيل الدخول",
    language: "English",
    eyebrow: "مصمم لرواد الأعمال العصريين",
    hero: "أدِر أعمالك بمنظومة واحدة متكاملة وقوية.",
    lead: "أنشئ مساحة عملك في Leylion بخطوتين فقط. بعد تأكيد بريدك تدخل مباشرة إلى النظام، وتكمل الإعداد الأساسي من داخل التطبيق.",
    connected: "مترابط",
    accountStep: "إنشاء الحساب",
    onlyTwoSteps: "خطوتان فقط",
    welcome: "مرحبًا بك في Leylion",
    startTitle: "ابدأ مع Leylion",
    startLead: "أدخل البيانات الأساسية فقط. الخطوة التالية هي تأكيد البريد، ثم تدخل Leylion مباشرة.",
    email: "بريد العمل الإلكتروني",
    emailPlaceholder: "owner@company.com",
    owner: "اسم المالك",
    ownerPlaceholder: "اسمك الكامل",
    business: "اسم المنشأة",
    businessPlaceholder: "اسم منشأتك",
    password: "كلمة المرور",
    passwordPlaceholder: "أنشئ كلمة مرور قوية",
    passwordHint: "10 أحرف على الأقل وتتضمن رقمًا ورمزًا.",
    termsPrefix: "أوافق على",
    terms: "شروط الخدمة",
    privacy: "سياسة الخصوصية",
    termsAnd: "و",
    start: "ابدأ مع Leylion ←",
    already: "لديك حساب بالفعل؟",
    verifyStep: "تأكيد البريد",
    finalStep: "الخطوة الأخيرة",
    secure: "تحقق بسيط وآمن",
    verifyTitle: "أكد بريدك الإلكتروني",
    verifyLead: "أرسلنا رمزًا من 6 أرقام إلى البريد التالي.",
    verify: "تأكيد والبدء ←",
    back: "رجوع",
    noCode: "لم يصلك الرمز؟",
    resend: "إعادة الإرسال",
    resendIn: "إعادة الإرسال خلال",
    seconds: "ث",
    resent: "تم إرسال رمز تحقق جديد.",
    preparing: "جاري تجهيز Leylion لك...",
    preparingWorkspace: "ننشئ منشأتك والفرع الأول ونفعّل الفترة التجريبية.",
    opening: "تم إنشاء مساحة العمل. جاري فتح Leylion...",
    invalidEmail: "أدخل بريد عمل إلكترونيًا صالحًا.",
    requiredNames: "أكمل اسم المالك واسم المنشأة.",
    weakPassword: "استخدم 10 أحرف على الأقل، تتضمن رقمًا ورمزًا.",
    acceptTerms: "وافق على شروط الخدمة وسياسة الخصوصية للمتابعة.",
    enterCode: "أدخل رمز التحقق المكوّن من 6 أرقام.",
    unexpected: "تعذر إكمال التسجيل الآن. حاول مرة أخرى.",
    contextDelay: "تم إنشاء الحساب، لكن مساحة العمل ما زالت تجهز. حاول تسجيل الدخول بعد لحظات.",
    trustOne: "عزل آمن لكل منشأة",
    trustTwo: "فترة تجريبية تلقائية",
    trustThree: "لا توجد بيانات تجريبية"
  },
  en: {
    signIn: "Sign in",
    language: "العربية",
    eyebrow: "Built for modern operators",
    hero: "Run your business with one integrated, powerful system.",
    lead: "Create your Leylion workspace in just two steps. Verify your email, enter the system, and finish the essential setup from inside the app.",
    connected: "Connected",
    accountStep: "Create account",
    onlyTwoSteps: "Only 2 steps",
    welcome: "Welcome to Leylion",
    startTitle: "Start with Leylion",
    startLead: "Enter only the essentials. Next, verify your email and go straight into Leylion.",
    email: "Work email",
    emailPlaceholder: "owner@company.com",
    owner: "Owner name",
    ownerPlaceholder: "Your full name",
    business: "Business name",
    businessPlaceholder: "Your business name",
    password: "Password",
    passwordPlaceholder: "Create a strong password",
    passwordHint: "At least 10 characters including a number and symbol.",
    termsPrefix: "I agree to the",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    termsAnd: "and",
    start: "Start with Leylion →",
    already: "Already have an account?",
    verifyStep: "Verify email",
    finalStep: "Final step",
    secure: "Simple & secure verification",
    verifyTitle: "Confirm your email",
    verifyLead: "We sent a 6-digit code to the following email.",
    verify: "Confirm & start →",
    back: "Back",
    noCode: "Didn’t receive the code?",
    resend: "Resend",
    resendIn: "Resend in",
    seconds: "s",
    resent: "A new verification code was sent.",
    preparing: "Preparing Leylion for you...",
    preparingWorkspace: "We’re creating your business, first branch and automatic trial.",
    opening: "Your workspace is ready. Opening Leylion...",
    invalidEmail: "Enter a valid work email.",
    requiredNames: "Complete the owner and business names.",
    weakPassword: "Use at least 10 characters with a number and symbol.",
    acceptTerms: "Accept the Terms of Service and Privacy Policy to continue.",
    enterCode: "Enter the 6-digit verification code.",
    unexpected: "Signup could not be completed right now. Please try again.",
    contextDelay: "Your account is ready, but the workspace is still preparing. Sign in again in a moment.",
    trustOne: "Secure tenant isolation",
    trustTwo: "Automatic trial",
    trustThree: "No demo data"
  }
} as const;

export type SignupText = (typeof uiCopy)[Language];

export interface EcosystemModuleCopy {
  title: string;
  sub: string;
  kicker: string;
  headline: string;
  body: string;
  flow: readonly [string, string, string];
}

export const moduleCopy: Record<Language, Record<ModuleKey, EcosystemModuleCopy>> = {
  ar: {
    sales: { title: "المبيعات", sub: "بيع وتحصيل", kicker: "المبيعات مترابطة", headline: "بع مرة واحدة، وحدّث كل شيء.", body: "كل عملية بيع تنعكس تلقائيًا على المخزون والعملاء والتقارير وعمليات الفروع.", flow: ["بيع", "مخزون", "تقارير"] },
    inventory: { title: "المخزون", sub: "مخزون متزامن", kicker: "المخزون مترابط", headline: "اعرف مخزونك لحظة بلحظة.", body: "المشتريات والمبيعات والتحويلات ونشاط الفروع كلها تتحدث مع نفس صورة المخزون.", flow: ["شراء", "مخزون", "بيع"] },
    branches: { title: "الفروع", sub: "تحكم موحد", kicker: "تحكم متعدد الفروع", headline: "منشأة واحدة، وكل الفروع مترابطة.", body: "يعمل كل فرع باستقلالية تشغيلية، بينما يحتفظ المالك بطبقة تحكم واحدة ومتصلة.", flow: ["فرع 01", "تحكم", "فرع 02"] },
    customers: { title: "العملاء", sub: "CRM وولاء", kicker: "العملاء مترابطون", headline: "حوّل العمليات إلى علاقات.", body: "سجل العميل والولاء والمبيعات يبقى مترابطًا دون تكرار العمل.", flow: ["عميل", "بيع", "ولاء"] },
    reports: { title: "التقارير", sub: "رؤية أوضح", kicker: "رؤية أعمال مباشرة", headline: "افهم ما يحدث في أعمالك.", body: "التقارير تعتمد على نفس البيانات التشغيلية من المبيعات والمخزون والفروع والعملاء.", flow: ["عمليات", "بيانات", "رؤية"] },
    team: { title: "الفريق", sub: "أدوار وورديات", kicker: "الفريق والصلاحيات", headline: "الصلاحية المناسبة لكل دور.", body: "المالك والمدير والموظفون يعملون داخل نفس النظام بصلاحيات وتحكم مناسب لكل دور.", flow: ["مالك", "أدوار", "فريق"] },
    operations: { title: "العمليات", sub: "إدارة التشغيل اليومي", kicker: "العمليات مترابطة", headline: "كل عملياتك في مسار واحد.", body: "الطلبات والخدمات والتنفيذ والمخزون والفروع تعمل معًا داخل منظومة تشغيل موحدة.", flow: ["طلب", "تنفيذ", "تشغيل"] },
    devices: { title: "الأجهزة", sub: "POS وطابعات", kicker: "أجهزة مترابطة", headline: "شغّل أعمالك أينما كانت.", body: "أجهزة نقطة البيع والطابعات ونقاط التشغيل كلها تتصل بمساحة أعمال واحدة ومحكومة.", flow: ["جهاز", "Leylion", "تشغيل"] }
  },
  en: {
    sales: { title: "Sales", sub: "Sell & collect", kicker: "SALES CONNECTED", headline: "Sell once. Update everywhere.", body: "Every sale flows through inventory, customers, reports and branch operations automatically.", flow: ["Sale", "Inventory", "Reports"] },
    inventory: { title: "Inventory", sub: "Stock in sync", kicker: "INVENTORY CONNECTED", headline: "Know your stock as business happens.", body: "Purchases, sales, transfers and branch activity stay connected to the same inventory picture.", flow: ["Purchase", "Stock", "Sale"] },
    branches: { title: "Branches", sub: "One control layer", kicker: "MULTI-BRANCH CONTROL", headline: "One business. Every branch connected.", body: "Keep branches independent operationally while owners retain one connected control layer.", flow: ["Branch 01", "Control", "Branch 02"] },
    customers: { title: "Customers", sub: "CRM & loyalty", kicker: "CRM CONNECTED", headline: "Turn transactions into relationships.", body: "Customer history, loyalty and sales activity stay connected without duplicating work.", flow: ["Customer", "Sale", "Loyalty"] },
    reports: { title: "Reports", sub: "Know what matters", kicker: "LIVE BUSINESS INSIGHT", headline: "Understand what the business is doing.", body: "Reports are fed by the same operational data across sales, inventory, branches and customers.", flow: ["Operations", "Data", "Insight"] },
    team: { title: "Team", sub: "Roles & shifts", kicker: "TEAM & ACCESS", headline: "The right access for every role.", body: "Owners, managers and staff work in the same system with role-aware permissions and controls.", flow: ["Owner", "Roles", "Team"] },
    operations: { title: "Operations", sub: "Daily workflows", kicker: "CONNECTED OPERATIONS", headline: "Keep every workflow moving together.", body: "Orders, services, fulfillment, inventory and branches stay connected in one operating system.", flow: ["Order", "Fulfillment", "Operations"] },
    devices: { title: "Devices", sub: "POS & printers", kicker: "CONNECTED DEVICES", headline: "Operate wherever the business runs.", body: "POS devices, printers and operational endpoints connect into one governed business workspace.", flow: ["Device", "Leylion", "Operations"] }
  }
};

export const moduleOrder: ModuleKey[] = [
  "sales",
  "inventory",
  "branches",
  "customers",
  "reports",
  "team",
  "operations",
  "devices"
];
